import moment, {type Moment} from 'moment-timezone';

import {type LeaveSubType} from 'web-app/react/leave-types/leave-subtype';
import {type LeaveType} from 'web-app/react/leave-types/leave-type';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {API_ZONED_DATE_TIME_FORMAT} from './constants';

/**
 * This is returned from the backend for both children and employees
 * hence why some of the fields are optional
 */
export interface AdaptedLeaveFromApi {
    duration: DurationType;
    leaveType: LeaveType;
    leaveSubType?: LeaveSubType;
    body: string;
    deleting: boolean;
    childLeaveIds?: (string | undefined)[];
    employeeLeaveIds?: string[];
    startDate: Moment;
    endDate: Moment | null;
    hours?: number;
    isPDL: boolean;
}

interface LeaveFromApi {
    childId?: string;
    employeeId?: string;
    employeeLeaveId?: string;
    childLeaveId?: string;
    day: string;
    date: string;
    deletedAt: string | null;
    employeeWorkDay: number | null;
    vacationId?: string | null;
    institutionId: string;
    leaveType: string;
    reason: string;
    leaveSubType?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    // this value is only returned for employees and not for children
    hours?: number;
}

export type LeaveDurationType = 'ABSTRACT_DAYS' | 'HOURS';

export interface DurationType {
    duration: {
        hours: number | null;
        days: number;
    };
    type: LeaveDurationType;
}
/**
 *
 * @param leavePeriod leave records that come back from the backend
 * @param category can either be a child leave or an employee leave
 * @returns DurationType that has {duration: number, and a type: DAYS or HOURS} that is used later on for formatting reasons
 */
const calculateFullDuration = (leavePeriod: LeaveFromApi[], category: LeaveCategory): DurationType => {
    if (category === 'CHILD_LEAVE') {
        // assume every leave is 8 hours for a child leave
        return {type: 'ABSTRACT_DAYS', duration: {days: leavePeriod.length, hours: null}};
    }

    // if the employee leave has no hours information in the leave
    if (category === 'EMPLOYEE_LEAVE' && !hasValue(leavePeriod[0].hours)) {
        return {type: 'ABSTRACT_DAYS', duration: {days: leavePeriod.length, hours: null}};
    }

    // if the employee leave has an hours field, use it
    // otherwise duration will be in days
    const type = leavePeriod.every(leave => hasValue(leave.hours)) ? 'HOURS' : 'ABSTRACT_DAYS';

    const durationInHours = leavePeriod.reduce(
        (acc, leave) => (hasValue(leave.hours) ? acc + leave.hours : leavePeriod.length),
        0,
    );

    /**
     * IF PDL feature is off -> we need to show things in days and not in hours for full day leaves (for Part day leaves we always show hours regardless)
     * This duration in days is always returned
     *
     * The reason we're using the length is because we are not able to answer this question (we have a value of 7 hours, but is that considered a full day for the nursery?)
     * leave in hours -> leave in days
     * a Full day leave was taken of 7 hours
     * The nursery has a time1 full working day length of 7 hours for example
     * in that case 7 hours -> 1 day
     * suppose now that the full working day length in the settings is changed to be 10 hours at time2
     * in that case the 1 day leave that contains the information of (7 hours) is not valid anymore. Whatever was valid at time1 is not valid in time2 anymore
     * so instead of doing assumption we just do this: if it was a FDL -> then it's an FDL now (1 leave = 1 day)
     * we don't want to show something like 0.75 days
     * so we're instead always falling back to (1 day) ----> regardless of what the definition of 1 day is (sticking with the relative value instead of going absolute here)
     */
    const durationInDays = leavePeriod.length;

    return {
        type,
        duration: {
            hours: durationInHours,
            days: durationInDays,
        },
    };
};

export type LeaveCategory = 'CHILD_LEAVE' | 'EMPLOYEE_LEAVE';

export const leavePeriodFromApi = (leavePeriod: LeaveFromApi[]): AdaptedLeaveFromApi => {
    const leaveCategory = hasValue(leavePeriod[0].childId) ? 'CHILD_LEAVE' : 'EMPLOYEE_LEAVE';
    const first = leavePeriod[0];
    const last = leavePeriod[leavePeriod.length - 1];

    // if endTime undefined then it's a legacy leave
    const legacyEndDate = leavePeriod.length > 1 ? moment(last.date) : null;

    const isPDL = hasValue(first.startTime) && hasValue(first.endTime);
    // if last is undefined that also means that we are dealing with a PDL
    return {
        startDate: hasValue(first.startTime) ? moment(first.startTime, API_ZONED_DATE_TIME_FORMAT) : moment(first.date),
        endDate: hasValue(last.endTime) ? moment(last.endTime, API_ZONED_DATE_TIME_FORMAT) : legacyEndDate,
        duration: calculateFullDuration(leavePeriod, leaveCategory),
        leaveType: first.leaveType as unknown as LeaveType,
        body: first.reason,
        deleting: false,
        childLeaveIds: leavePeriod.map(day => day.childLeaveId),
        employeeLeaveIds: leavePeriod.map(day => day.employeeLeaveId).filter(hasValue),
        leaveSubType: first.leaveSubType as unknown as LeaveSubType,
        isPDL,
    };
};
